import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { IataAirline } from "../../iata/objects/iataAirline";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_IATA_AIRLINE_LIST_CONFIGURATION, EMPTY_IATA_AIRLINE_LIST_CRITERIA } from "../constant/iataAirlineConstant";
import { Column } from "../objects/column";
import { IataAirlineListCriteria } from "../objects/iataAirlineListCriteria";
import { IataAirlineState } from "../objects/iataAirlineState";
import { setIataAirlinePreference } from "../utils/setIataAirlinePreference";

const initialState: IataAirlineState = {
    iata_airline_list: [],
    iata_airline_list_loading: false,
    iata_airline_list_criteria: EMPTY_IATA_AIRLINE_LIST_CRITERIA,
    iata_airline_list_configuration: EMPTY_IATA_AIRLINE_LIST_CONFIGURATION,
    iata_airline_list_count: 0,
    iata_airline: null,
    iata_airline_delete: null,
    iata_airline_refresh: false,
    first_preference_loaded: false,
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

// ── Reducer functions ─────────────────────────────────────────────────────────

const updateIataAirlineListReducer = (state: IataAirlineState, action: PayloadAction<IataAirline[]>): IataAirlineState =>
    ({ ...state, iata_airline_list: action.payload });

const updateIataAirlineListLoadingReducer = (state: IataAirlineState, action: PayloadAction<boolean>): IataAirlineState =>
    ({ ...state, iata_airline_list_loading: action.payload });

const updateIataAirlineListCriteriaReducer = (state: IataAirlineState, action: PayloadAction<IataAirlineListCriteria>): IataAirlineState =>
    ({ ...state, iata_airline_list_criteria: action.payload });

const updateIataAirlineListCountReducer = (state: IataAirlineState, action: PayloadAction<number>): IataAirlineState =>
    ({ ...state, iata_airline_list_count: action.payload });

const toggleIataAirlineRefreshReducer = (state: IataAirlineState): IataAirlineState =>
    ({ ...state, iata_airline_refresh: !state.iata_airline_refresh });

const updateIataAirlineReducer = (state: IataAirlineState, action: PayloadAction<IataAirline | null>): IataAirlineState =>
    ({ ...state, iata_airline: action.payload });

const setIataAirlineDeleteReducer = (state: IataAirlineState, action: PayloadAction<IataAirline | null>): IataAirlineState =>
    ({ ...state, iata_airline_delete: action.payload });

const addIataAirlineListConfigurationPreferenceReducer = (state: IataAirlineState, action: PayloadAction<Preferences[]>): IataAirlineState => ({
    ...state, iata_airline_list_configuration: { ...state.iata_airline_list_configuration, preferences: [...state.iata_airline_list_configuration.preferences, ...action.payload] }
});

const setIataAirlineListConfigurationPreferenceReducer = (state: IataAirlineState, action: PayloadAction<Preferences[]>): IataAirlineState => ({
    ...state, iata_airline_list_configuration: { ...state.iata_airline_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: IataAirlineState, action: PayloadAction<Preferences[]>): IataAirlineState => {
    const { preferences, columns, order_by, order } = setIataAirlinePreference(action.payload, state.iata_airline_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        iata_airline_list_configuration: { ...state.iata_airline_list_configuration, preferences, columns },
        iata_airline_list_criteria: { ...state.iata_airline_list_criteria, order, order_by }
    };
};

const toggleColumnReducer = (state: IataAirlineState, action: PayloadAction<number>): IataAirlineState => {
    const columns = cloneDeep([...state.iata_airline_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, iata_airline_list_configuration: { ...state.iata_airline_list_configuration, columns } };
};

const setColumnsReducer = (state: IataAirlineState, action: PayloadAction<Column[]>): IataAirlineState => ({
    ...state, iata_airline_list_configuration: { ...state.iata_airline_list_configuration, columns: action.payload },
});

// ── Slice ─────────────────────────────────────────────────────────────────────

export const iataAirline = createSlice({
    name: 'iata_airline',
    initialState,
    reducers: {
        updateIataAirlineList: updateIataAirlineListReducer,
        updateIataAirlineListLoading: updateIataAirlineListLoadingReducer,
        updateIataAirlineListCriteria: updateIataAirlineListCriteriaReducer,
        updateIataAirlineListCount: updateIataAirlineListCountReducer,
        toggleIataAirlineRefresh: toggleIataAirlineRefreshReducer,
        updateIataAirline: updateIataAirlineReducer,
        setIataAirlineDelete: setIataAirlineDeleteReducer,
        addIataAirlineListConfigurationPreference: addIataAirlineListConfigurationPreferenceReducer,
        setIataAirlineListConfigurationPreference: setIataAirlineListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        setColumns: setColumnsReducer,
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    updateIataAirlineList,
    updateIataAirlineListLoading,
    updateIataAirlineListCriteria,
    updateIataAirlineListCount,
    toggleIataAirlineRefresh,
    updateIataAirline,
    setIataAirlineDelete,
    addIataAirlineListConfigurationPreference,
    setIataAirlineListConfigurationPreference,
    toggleColumn,
    setColumns,
} = iataAirline.actions;

export default iataAirline.reducer;
